import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Avatar,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Stack,
  Divider,
  Alert,
  Button,
  Tooltip,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReplayIcon from "@mui/icons-material/Replay";
import api from "../../utils/api";

// ------------------------
// Badge helper
// ------------------------
function getWeeklyBadge(rank) {
  const r = Number(rank);
  if (!r) return null;
  if (r === 1) return { label: "🏆 Weekly Champion", color: "warning", variant: "filled" };
  if (r <= 3) return { label: "🔥 Elite Performer", color: "error", variant: "filled" };
  if (r <= 10) return { label: "⭐ Top 10 Warrior", color: "success", variant: "outlined" };
  return null;
}

// ------------------------
// Trend helper (UP/DOWN/SAME/NEW)
// ------------------------
function getTrendMeta(trend, currentRank, previousRank) {
  const t = (trend || "").toUpperCase();

  if (t === "UP") {
    return {
      icon: "⬆️",
      label: previousRank ? `Up from #${previousRank} → #${currentRank}` : "Trending up",
      color: "success",
      variant: "outlined",
    };
  }
  if (t === "DOWN") {
    return {
      icon: "⬇️",
      label: previousRank ? `Down from #${previousRank} → #${currentRank}` : "Trending down",
      color: "error",
      variant: "outlined",
    };
  }
  if (t === "SAME") {
    return {
      icon: "➡️",
      label: previousRank ? `No change (#${currentRank})` : "No change",
      color: "info",
      variant: "outlined",
    };
  }
  if (t === "NEW") {
    return {
      icon: "🆕",
      label: "New on the leaderboard this week",
      color: "warning",
      variant: "outlined",
    };
  }

  return {
    icon: "➡️",
    label: "Trend unavailable",
    color: "default",
    variant: "outlined",
  };
}

// ------------------------
// number helpers
// ------------------------
function safeNum(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
}

function fmt2(n) {
  const x = safeNum(n);
  if (x === null) return "—";
  return x.toFixed(2);
}

// ------------------------
// Date helpers
// ------------------------
function formatDateTime(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(); // local time display
}

// ------------------------
// Countdown hook
// ------------------------
function useCountdown(endTime) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!endTime) return;

    const tick = () => {
      const end = new Date(endTime).getTime();
      const now = Date.now();
      const diff = end - now;

      if (!Number.isFinite(end) || diff <= 0) {
        setTimeLeft("Ended");
        return;
      }

      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);

      const parts = [];
      if (d > 0) parts.push(`${d}d`);
      parts.push(`${h}h`, `${m}m`, `${s}s`);
      setTimeLeft(parts.join(" "));
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  return timeLeft;
}

export default function WeeklyChallengeBase({ examType, subjectId, title, subtitle }) {
  // ✅ IMPORTANT: DO NOT early-return BEFORE hooks.
  // We'll render the alert later, after hooks.

  // (1) Week info (countdown support)
  const [weekLoading, setWeekLoading] = useState(true);
  const [weekInfo, setWeekInfo] = useState(null);

  // weekly leaderboard
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // (2) My rank this week
  const [myRankLoading, setMyRankLoading] = useState(true);
  const [myRank, setMyRank] = useState(null);

  const countdown = useCountdown(weekInfo?.week_end);

  // ✅ Champions should follow ranked output (backend returns already ranked)
  const champions = useMemo(() => rows.slice(0, 3), [rows]);

  const loadWeekInfo = async () => {
    setWeekLoading(true);
    try {
      const { data } = await api.get("/leaderboard/week-info");
      setWeekInfo(data || null);
    } catch (err) {
      console.error("Week info load error:", err);
      setWeekInfo(null);
    } finally {
      setWeekLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    if (!examType) return;

    setLoading(true);
    try {
      const url = subjectId
        ? `/leaderboard/weekly?exam_type=${examType}&subject_id=${subjectId}`
        : `/leaderboard/weekly?exam_type=${examType}`;

      const { data } = await api.get(url);
      setRows(Array.isArray(data?.leaderboard) ? data.leaderboard : []);
    } catch (err) {
      console.error("Weekly leaderboard load error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMyRank = async () => {
    // ✅ guard API call only (NOT hooks)
    if (!examType) {
      setMyRank(null);
      setMyRankLoading(false);
      return;
    }

    setMyRankLoading(true);
    try {
      const { data } = await api.get(`/leaderboard/weekly/my-rank?exam_type=${examType}`);
      setMyRank(data || null);
    } catch (err) {
      console.error("Weekly my-rank load error:", err);
      setMyRank(null);
    } finally {
      setMyRankLoading(false);
    }
  };

  const refreshAll = async () => {
    // ✅ if missing examType, don't call API
    if (!examType) {
      setWeekLoading(false);
      setLoading(false);
      setMyRankLoading(false);
      setWeekInfo(null);
      setRows([]);
      setMyRank(null);
      return;
    }

    // keep resilient: one failure won’t block others
    await loadWeekInfo();
    await loadLeaderboard();
    await loadMyRank();
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examType, subjectId]);

  // ✅ Now we can render this safely AFTER hooks have been called
  if (!examType) {
    return (
      <Alert severity="error">
        WeeklyChallengeBase requires an <strong>examType</strong> prop.
        <br />
        Fix your route to render <strong>WeeklySubjectChallenge</strong> or{" "}
        <strong>WeeklyPostUTMEChallenge</strong> (not WeeklyChallengeBase directly).
      </Alert>
    );
  }

  // Loading state: show a clean loader first
  if (loading && weekLoading && myRankLoading) {
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
        <Typography mt={2}>Loading weekly challenge…</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2.5, md: 3 },
        py: { xs: 2, sm: 3 },
        overflowX: "hidden",
      }}
    >
      {/* ===== HEADER ===== */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        </Box>

        <Button
          size="small"
          variant="outlined"
          startIcon={<ReplayIcon />}
          onClick={refreshAll}
          sx={{ fontWeight: 800 }}
        >
          Refresh
        </Button>
      </Stack>

      {/* ===== (1) WEEK WINDOW + COUNTDOWN ===== */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <CalendarMonthIcon color="action" />
            <Box>
              <Typography fontWeight={900}>This Week Window</Typography>
              <Typography variant="body2" color="text.secondary">
                {weekInfo
                  ? `${formatDateTime(weekInfo.week_start)} → ${formatDateTime(weekInfo.week_end)}`
                  : "Week info unavailable"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip
              label={countdown ? `⏳ Ends in ${countdown}` : "⏳ Calculating time..."}
              color="warning"
              sx={{ fontWeight: 900 }}
            />
            <Chip
              label="🔁 Resets every Monday"
              color="info"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            <Chip label={`🏷️ ${examType}`} variant="outlined" sx={{ fontWeight: 800 }} />
          </Stack>
        </Stack>
      </Paper>

      {/* ===== (2) MY RANK THIS WEEK ===== */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Typography fontWeight={900} sx={{ mb: 1 }}>
          👤 My Rank This Week
        </Typography>

        {myRankLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading your weekly rank…
          </Typography>
        ) : myRank?.rank ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip
              label={`Rank #${myRank.rank}${myRank.total_students ? ` / ${myRank.total_students}` : ""}`}
              color="primary"
              sx={{ fontWeight: 900 }}
            />
            <Chip
              label={`Best: ${myRank.best_score ?? "—"}%`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={`Avg: ${myRank.avg_score ?? "—"}%`}
              color="info"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            {myRank.percentile && (
              <Chip
                label={`Percentile: ${myRank.percentile}`}
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
            )}

            {getWeeklyBadge(myRank.rank) && (
              <Chip
                label={getWeeklyBadge(myRank.rank).label}
                color={getWeeklyBadge(myRank.rank).color}
                variant={getWeeklyBadge(myRank.rank).variant}
                sx={{ fontWeight: 900 }}
              />
            )}
          </Stack>
        ) : (
          <Alert severity="info">
            You have no submitted exam for this week yet. Take an exam to enter the leaderboard.
          </Alert>
        )}
      </Paper>

      {/* ===== EMPTY WEEKLY DATA ===== */}
      {!loading && (!rows || rows.length === 0) && (
        <Alert severity="info">No submissions yet this week. Be the first to take the lead!</Alert>
      )}

      {/* ===== DATA LOADING (TABLE/CHAMPIONS) ===== */}
      {loading && (
        <Box textAlign="center" mt={4}>
          <CircularProgress />
          <Typography mt={2}>Loading weekly leaderboard…</Typography>
        </Box>
      )}

      {/* ===== CONTENT ===== */}
      {!loading && rows && rows.length > 0 && (
        <Box>
          {/* ===== TOP CHAMPIONS ===== */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 900 }}>
            🔥 This Week’s Leaders
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
            {champions.map((item, index) => {
              const name = item.full_name || "Student";
              const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
              const badge = getWeeklyBadge(item.current_rank ?? item.rank);
              const trendMeta = getTrendMeta(
                item.trend,
                item.current_rank ?? item.rank,
                item.previous_rank
              );

              return (
                <Paper
                  key={`${item.student_id}-${item.current_rank ?? item.rank}`}
                  elevation={3}
                  sx={{
                    p: 2,
                    flex: 1,
                    borderRadius: 3,
                    textAlign: "center",
                    borderTop: `6px solid ${medalColors[index]}`,
                  }}
                >
                  <EmojiEventsIcon sx={{ color: medalColors[index], fontSize: 36, mb: 1 }} />

                  <Avatar
                    sx={{
                      mx: "auto",
                      bgcolor: "primary.main",
                      width: 56,
                      height: 56,
                      fontSize: 22,
                    }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>

                  <Typography mt={1} fontWeight={900}>
                    {name}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    flexWrap="wrap"
                    sx={{ mt: 1 }}
                  >
                    <Chip
                      label={`Best: ${item.best_score ?? "—"}%`}
                      size="small"
                      color="success"
                      sx={{ fontWeight: 900 }}
                    />
                    <Chip
                      label={`Avg: ${item.avg_score ?? "—"}%`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 900 }}
                    />
                    {item.weighted_score !== undefined && item.weighted_score !== null && (
                      <Chip
                        label={`Weighted: ${fmt2(item.weighted_score)}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 900 }}
                      />
                    )}
                    {item.subject_name && (
                      <Chip
                        label={`${item.subject_name}${item.subject_code ? ` (${item.subject_code})` : ""}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 900 }}
                      />
                    )}
                    {badge && (
                      <Chip
                        label={badge.label}
                        size="small"
                        color={badge.color}
                        variant={badge.variant}
                        sx={{ fontWeight: 900 }}
                      />
                    )}
                    <Tooltip title={trendMeta.label}>
                      <Chip
                        label={trendMeta.icon}
                        size="small"
                        color={trendMeta.color}
                        variant={trendMeta.variant}
                        sx={{ fontWeight: 900 }}
                      />
                    </Tooltip>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* ===== FULL TABLE ===== */}
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={100}>Rank</TableCell>
                    <TableCell>Student</TableCell>

                    {/* NEW: Trend */}
                    <TableCell width={90}>Trend</TableCell>

                    {/* NEW: Subject (dynamic) */}
                    <TableCell width={200}>Subject</TableCell>

                    {/* existing + improved */}
                    <TableCell width={140}>Best Score</TableCell>
                    <TableCell width={140}>Avg Score</TableCell>
                    <TableCell width={160}>Weighted</TableCell>
                    <TableCell width={120}>Exams</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.slice(0, 10).map((item) => {
                    const name = item.full_name || "Student";
                    const rank = item.current_rank ?? item.rank;
                    const badge = getWeeklyBadge(rank);
                    const trendMeta = getTrendMeta(item.trend, rank, item.previous_rank);

                    return (
                      <TableRow hover key={`${item.student_id}-${rank}`}>
                        <TableCell>
                          <Typography fontWeight={900}>#{rank}</Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Avatar sx={{ bgcolor: "primary.main" }}>{name.charAt(0)}</Avatar>
                            <Typography sx={{ fontWeight: 700 }}>{name}</Typography>
                            {badge && (
                              <Chip
                                label={badge.label}
                                size="small"
                                color={badge.color}
                                variant="outlined"
                                sx={{ fontWeight: 900 }}
                              />
                            )}
                          </Stack>
                        </TableCell>

                        {/* Trend */}
                        <TableCell>
                          <Tooltip title={trendMeta.label}>
                            <Chip
                              label={trendMeta.icon}
                              size="small"
                              color={trendMeta.color}
                              variant={trendMeta.variant}
                              sx={{ fontWeight: 900 }}
                            />
                          </Tooltip>
                        </TableCell>

                        {/* Subject */}
                        <TableCell>
                          {item.subject_name ? (
                            <Chip
                              label={`${item.subject_name}${item.subject_code ? ` (${item.subject_code})` : ""}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 900 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Best */}
                        <TableCell>
                          <Chip
                            label={`${item.best_score ?? "—"}%`}
                            color="success"
                            variant="outlined"
                            sx={{ fontWeight: 900 }}
                          />
                        </TableCell>

                        {/* Avg */}
                        <TableCell>
                          <Chip
                            label={`${item.avg_score ?? "—"}%`}
                            color="info"
                            variant="outlined"
                            sx={{ fontWeight: 900 }}
                          />
                        </TableCell>

                        {/* Weighted */}
                        <TableCell>
                          <Chip
                            label={
                              item.weighted_score !== undefined && item.weighted_score !== null
                                ? fmt2(item.weighted_score)
                                : "—"
                            }
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 900 }}
                          />
                        </TableCell>

                        {/* Exams */}
                        <TableCell>
                          <Chip
                            label={item.exams_taken ?? 0}
                            variant="outlined"
                            sx={{ fontWeight: 900 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Ranking is based on <strong>Weighted Score</strong> (Best × Avg). Trend compares this week’s rank to last week.
              Badges reset automatically each new week.
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
